'use client'

import React, { useEffect, useSyncExternalStore, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useStytchMember } from '@stytch/nextjs/b2b'
import { useTenant } from '@/components/TenantLayout/Client'
import { getTenantContactEmail } from '@/components/ui/TenantContactInfo'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { isAuthPagePath, isFixDataPath, PATIENT_PATHS } from '@/utilities/paths'
import { isClientPortal } from '@/utilities/stytch/config'
import { Plus } from 'lucide-react'
import AccountMenu from '@/components/shared/navigation/AccountMenu'
import { SupportEmailLink } from '@/components/shared/SupportEmailLink'
import { FormLinkButton } from '@/components/ui/forms'
import { LanguageDropdown } from '@/components/ui/LanguageDropdown'

// Stable module-level reference required by useSyncExternalStore — an inline lambda would
// create a new function on every render, which React treats as a changed subscriber.
const NOOP_SUBSCRIBE = () => () => {}

export function HeaderRight() {
  const { tenant, user, isOnboarding } = useTenant()
  const pathname = usePathname()
  const isNonAuthPath = !isAuthPagePath(pathname)
  // useSyncExternalStore: server snapshot = false (prevents hydration mismatch), client
  // snapshot = true (can safely read Stytch session). Replaces the useEffect+setState pattern
  // that caused an extra client re-render and the react-hooks/set-state-in-effect suppression.
  const canReadStytchSession = useSyncExternalStore(
    NOOP_SUBSCRIBE,
    () => true,
    () => false,
  )
  const [hasStytchSession, setHasStytchSession] = useState(false)

  const hasSession = Boolean(user) || hasStytchSession
  const showNav =
    hasSession && !isOnboarding && isNonAuthPath && pathname !== PATIENT_PATHS.hipaaConsent

  return (
    <div className="flex items-center gap-4 shrink-0">
      {showNav && (
        <div className="hidden sm:contents">
          <FormLinkButton
            href={PATIENT_PATHS.flows.kitRegistrationFirstStep}
            variant="primary"
            size="sm"
            borderRadius="full"
            className="inline-flex items-center gap-2 px-5"
            data-testid="patient-header-register-kit"
            onClick={() =>
              trackEvent('navigation:register_kit_click', { placement: 'header_desktop' })
            }
          >
            Register Kit
            <Plus className="size-4 shrink-0" aria-hidden="true" />
          </FormLinkButton>
        </div>
      )}
      {showNav && <div className="hidden sm:block w-px h-4 bg-border" aria-hidden="true" />}
      {tenant?.slug !== 'client' && <LanguageDropdown />}
      {hasSession && isNonAuthPath && (
        <AccountMenu profileHref={showNav ? PATIENT_PATHS.userProfile : undefined} />
      )}
      {isFixDataPath(pathname) ? (
        <SupportEmailLink
          email={getTenantContactEmail(tenant ?? undefined)}
          className="text-sm leading-4 text-tenant-hyperlink!"
          data-testid="correction-header-need-help"
          onClick={() => {
            if (!isClientPortal()) {
              trackEvent('patient:address_correction:need_help_click')
            }
          }}
        >
          Need help?
        </SupportEmailLink>
      ) : null}
      {canReadStytchSession && !user && (
        <HeaderStytchSessionProbe onSessionChange={setHasStytchSession} />
      )}
    </div>
  )
}

function HeaderStytchSessionProbe({
  onSessionChange,
}: Readonly<{ onSessionChange: (hasSession: boolean) => void }>) {
  const { member, isInitialized } = useStytchMember()

  useEffect(() => {
    if (isInitialized) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent -- data passed to parent from an effect; keep until refactor
      onSessionChange(Boolean(member))
    }
  }, [isInitialized, member, onSessionChange])

  return null
}
